/**
 * OBG Bulk Card Search — card-search.js
 * Place in: assets/card-search.js
 *
 * Requires:
 *  - window.OBG_STOREFRONT_TOKEN  (public Storefront API token from Headless channel)
 *  - window.OBG_SHOP_DOMAIN       (e.g. "your-store.myshopify.com")
 *
 * Both are injected by page.card-search.liquid.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     CONFIG
  ───────────────────────────────────────────── */
  const STOREFRONT_API_VERSION = '2026-04';
  const PRODUCTS_PER_QUERY     = 10;  // max products fetched per card name search (stock alias)
  const ANY_PRODUCTS_PER_QUERY = 10;  // max products fetched for the unfiltered "any" alias — needs breadth because Shopify title search is substring-based and the exact-name match may not be in the top 1 result
  const ALIASES_PER_REQUEST    = 10;  // cards per HTTP request via GraphQL field aliasing
  const CONCURRENT_REQUESTS    = 3;   // batched HTTP requests in flight at once
  const MAX_CARDS              = 100; // max cards per search to avoid rate limiting

  /* ─────────────────────────────────────────────
     DOM REFS
  ───────────────────────────────────────────── */
  const input     = document.getElementById('obg-cs-input');
  const searchBtn = document.getElementById('obg-cs-search');
  const clearBtn  = document.getElementById('obg-cs-clear');
  const results   = document.getElementById('obg-cs-results');

  /* ─────────────────────────────────────────────
     STATE
  ───────────────────────────────────────────── */
  // rowState[rowId] = { printingIdx, variantId, maxQty, qty, requestedQty, stockKnown }
  const rowState = {};

  /* ─────────────────────────────────────────────
     PARSE
  ───────────────────────────────────────────── */

  /**
   * Parse a single MTGO/Moxfield export line.
   * Handles:
   *   4 Lightning Bolt
   *   1 Black Lotus (LEA)
   *   2 Snapcaster Mage (ISD) 78 *F*
   *   1 Acrobatic Cheerleader [Duskmourn: House of Horror]
   */
  function parseLine(line) {
    line = line.trim();
    if (!line || line.startsWith('//')) return null;

    const foil = /\*F\*/i.test(line);
    line = line.replace(/\*F\*/gi, '').trim();

    // Normalize "1x Card" / "1X Card" export formats (TappedOut/Archidekt/etc.)
    // to the plain "1 Card" shape that the regexes below expect. The x must be
    // attached to the digit so cards like "X Factor" aren't mis-stripped.
    line = line.replace(/^(\d+)x\s+/i, '$1 ');

    // Handle Moxfield/MTGO bracket set name format: "1 Acrobatic Cheerleader [Duskmourn: House of Horror]"
    // Strip the bracket set name entirely — we extract the card name before it
    // and use it for matching against our title format "Card Name [Set Name]"
    const bracketMatch = line.match(/^(\d+)\s+(.+?)\s+\[([^\]]+)\]\s*$/);
    if (bracketMatch) {
      return {
        qty:     parseInt(bracketMatch[1], 10),
        name:    bracketMatch[2].trim(),
        setCode: null,
        setName: bracketMatch[3].trim(), // e.g. "Duskmourn: House of Horror"
        foil,
      };
    }

    // Standard MTGO format: "1 Card Name (SET) 123"
    const match = line.match(/^(\d+)\s+(.+?)(?:\s+\(([A-Za-z0-9]+)\))?(?:\s+\d+)?\s*$/);
    if (!match) return null;

    return {
      qty:     parseInt(match[1], 10),
      name:    match[2].trim(),
      setCode: match[3] ? match[3].toUpperCase() : null,
      setName: null,
      foil,
    };
  }

  function parseInput(raw) {
    return raw
      .split('\n')
      .map(line => {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('//')) return null;
        const parsed = parseLine(trimmed);
        if (!parsed) return { unrecognized: true, raw: trimmed };
        return parsed;
      })
      .filter(Boolean);
  }

  /* ─────────────────────────────────────────────
     STOREFRONT API
  ───────────────────────────────────────────── */

  function storefrontFetch(query, variables = {}) {
    const token  = window.OBG_STOREFRONT_TOKEN;
    const domain = window.OBG_SHOP_DOMAIN;

    if (!token || !domain) {
      return Promise.reject(new Error(
        'Storefront API token or shop domain is not configured. ' +
        'Check OBG_STOREFRONT_TOKEN value in page.card-search.liquid.'
      ));
    }

    return fetch(`https://${domain}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
      method:  'POST',
      headers: {
        'Content-Type':                      'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query, variables }),
    })
      .then(r => {
        if (r.status === 429) {
          const err = new Error('Rate limit exceeded');
          err.isRateLimit = true;
          throw err;
        }
        return r.json();
      })
      .then(json => {
        if (json.errors) {
          console.error('[OBG Card Search] Storefront API errors:', json.errors);
        }
        if (!json.data) {
          const msg = json.errors ? json.errors.map(e => e.message).join('; ') : 'No data returned';
          throw new Error(msg);
        }
        return json.data;
      });
  }

  const PRODUCT_FRAGMENT = `
    fragment ProductFields on Product {
      id
      title
      variants(first: 30) {
        edges {
          node {
            id
            title
            price { amount currencyCode }
            quantityAvailable
            availableForSale
            currentlyNotInStock
          }
        }
      }
    }
  `;

  /**
   * Builds a GraphQL query that looks up multiple card names in a single request
   * using field aliases. For each card we fire two aliased queries:
   *   cardN_stock — up to PRODUCTS_PER_QUERY printings that have stock
   *   cardN_any   — one printing regardless of stock (so we can show "sold out"
   *                 when the card exists in the catalog but has no inventory).
   * This single-request dual-alias shape keeps latency flat regardless of how
   * many cards in the paste are actually in stock.
   */
  function buildBatchQuery(count) {
    const varDefs = Array.from({ length: count }, (_, i) =>
      `$qs${i}: String!, $qa${i}: String!`
    ).join(', ');
    const fields  = Array.from({ length: count }, (_, i) =>
      `card${i}_stock: products(first: ${PRODUCTS_PER_QUERY}, query: $qs${i}) { edges { node { ...ProductFields } } }
        card${i}_any:   products(first: ${ANY_PRODUCTS_PER_QUERY}, query: $qa${i}) { edges { node { ...ProductFields } } }`
    ).join('\n      ');
    return `
      ${PRODUCT_FRAGMENT}
      query BatchSearch(${varDefs}) {
        ${fields}
      }
    `;
  }

  /**
   * Search a batch of cards in a single HTTP request using aliased fields.
   * Returns [{ parsed, printings, error? }] in the same order as the input.
   * Individual alias failures (data[alias] === null) are marked as error on that card only.
   */
  function searchBatch(cards) {
    const query     = buildBatchQuery(cards.length);
    const variables = {};
    cards.forEach((c, i) => {
      const escaped = c.name.replace(/"/g, '\\"');
      variables[`qs${i}`] = `title:"${escaped}" AND available_for_sale:true`;
      variables[`qa${i}`] = `title:"${escaped}"`;
    });
    return storefrontFetch(query, variables).then(data =>
      cards.map((parsed, i) => {
        const stockNode = data[`card${i}_stock`];
        const anyNode   = data[`card${i}_any`];
        if (!stockNode && !anyNode) return { parsed, printings: [], error: true };
        const stockProducts = stockNode ? stockNode.edges.map(e => e.node) : [];
        const anyProducts   = anyNode   ? anyNode.edges.map(e => e.node)   : [];
        // Merge de-duped by product id. In-stock printings take precedence so
        // findPrintings sees the in-stock variants first; any-printings only
        // surface a "sold out" row when the card isn't carried in stock.
        const byId = new Map();
        for (const p of stockProducts) byId.set(p.id, p);
        for (const p of anyProducts)   if (!byId.has(p.id)) byId.set(p.id, p);
        return { parsed, printings: findPrintings(parsed, Array.from(byId.values())) };
      })
    );
  }

  /* ─────────────────────────────────────────────
     MATCHING LOGIC
  ───────────────────────────────────────────── */

  /**
   * Parse a product title into its components.
   *   "Two-Headed Giant [Dominaria]"
   *     → { cardName: "Two-Headed Giant", setName: "Dominaria", treatment: null }
   *   "Vivi's Thunder Magic - Lightning Bolt (Rainbow Foil) [Secret Lair Drop Series]"
   *     → { cardName: "Vivi's Thunder Magic - Lightning Bolt",
   *         setName:  "Secret Lair Drop Series",
   *         treatment: "Rainbow Foil" }
   * Treatment is a trailing parenthetical on the card name used by OBG for
   * Secret Lair / special-treatment products (Rainbow Foil, Extended Art, etc).
   */
  function parseTitleParts(title) {
    const match = title.match(/^(.+?)\s*\[(.+)\]\s*$/);
    const rawName = match ? match[1].trim() : title.trim();
    const setName = match ? match[2].trim() : null;
    const treatMatch = rawName.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
    if (treatMatch) {
      return { cardName: treatMatch[1].trim(), setName, treatment: treatMatch[2].trim() };
    }
    return { cardName: rawName, setName, treatment: null };
  }

  /**
   * Given a parsed input line and an array of Shopify products,
   * find printings (products) and for each printing find in-stock variants.
   *
   * Strategy:
   *   1. Filter products whose cardName matches input name (case-insensitive)
   *   2. Among matches, flag those whose setName matches input setCode or setName (loose match)
   *   3. Filter to printings with at least one in-stock variant
   *   4. Sort: set matches first, then fallbacks
   */
  function findPrintings(parsed, products) {
    const inputName    = parsed.name.toLowerCase();
    const inputSetCode = parsed.setCode ? parsed.setCode.toLowerCase() : null;
    const inputSetName = parsed.setName ? parsed.setName.toLowerCase() : null;

    const printings = [];

    for (const product of products) {
      const { cardName, setName, treatment } = parseTitleParts(product.title);

      // Card name must match exactly (parseTitleParts has already stripped
      // any trailing treatment parenthetical from cardName).
      if (cardName.toLowerCase() !== inputName) continue;

      // Filter variants to in-stock only
      const inStockVariants = product.variants.edges
        .map(e => e.node)
        .filter(v => v.availableForSale && !v.currentlyNotInStock);

      // Loose set match: setCode appears in setName, OR full setName matches
      const productSetName = (setName || '').toLowerCase();
      const isSetMatch = inputSetCode
        ? productSetName.includes(inputSetCode)
        : inputSetName
          ? productSetName.includes(inputSetName) || inputSetName.includes(productSetName)
          : false;

      const displaySetName = setName || product.title;

      if (inStockVariants.length === 0) {
        printings.push({
          product,
          setName:    displaySetName,
          treatment,
          variants:   [],
          inStock:    false,
          isSetMatch,
        });
        continue;
      }

      printings.push({
        product,
        setName:    displaySetName,
        treatment,
        variants:   inStockVariants,
        inStock:    true,
        isSetMatch,
      });
    }

    // Sort: in-stock set matches → in-stock fallbacks → OOS set matches → OOS fallbacks
    printings.sort((a, b) => {
      if (a.inStock !== b.inStock)       return a.inStock ? -1 : 1;
      if (a.isSetMatch !== b.isSetMatch) return a.isSetMatch ? -1 : 1;
      return 0;
    });

    return printings;
  }

  /* ─────────────────────────────────────────────
     MAIN SEARCH FLOW
  ───────────────────────────────────────────── */

  async function runSearch() {
    const raw = input.value.trim();
    if (!raw) return;

    const allLines = parseInput(raw);
    if (!allLines.length) {
      showError('No card lines found. Check the format and try again.');
      return;
    }

    // Separate valid parsed lines from unrecognized ones
    const parsed       = allLines.filter(l => !l.unrecognized);
    const unrecognized = allLines.filter(l => l.unrecognized);

    if (!parsed.length) {
      showError('None of the lines could be read. Expected format: <strong>4 Card Name (SET) *F*</strong>');
      return;
    }

    // Enforce card limit
    if (parsed.length > MAX_CARDS) {
      showError(`Your list contains ${parsed.length} cards. Please limit searches to ${MAX_CARDS} cards at a time to ensure reliable results.`);
      return;
    }

    setSearching(true);
    results.innerHTML = renderSkeletons(parsed.length + unrecognized.length);
    Object.keys(rowState).forEach(k => delete rowState[k]);

    try {
      // Chunk cards into aliased batches (one HTTP request per chunk)
      const chunks = [];
      for (let i = 0; i < parsed.length; i += ALIASES_PER_REQUEST) {
        chunks.push(parsed.slice(i, i + ALIASES_PER_REQUEST));
      }

      // Run chunks with a concurrency cap so we don't drain the rate bucket in one burst
      const cardResults = [];
      for (let i = 0; i < chunks.length; i += CONCURRENT_REQUESTS) {
        const round = chunks.slice(i, i + CONCURRENT_REQUESTS);
        const roundResults = await Promise.all(
          round.map(async (chunk) => {
            try {
              return await searchBatch(chunk);
            } catch (err) {
              if (err.isRateLimit) throw err; // bubble up rate limit errors
              console.error('OBG card search batch error:', err);
              return chunk.map(p => ({ parsed: p, printings: [], error: true }));
            }
          })
        );
        roundResults.forEach(arr => cardResults.push(...arr));
      }

      renderResults(cardResults, unrecognized);
    } catch (err) {
      if (err.isRateLimit) {
        showError('Too many requests — our store API limit was reached. Please wait a moment and try again. If you have a large list, try splitting it into smaller batches.');
      } else {
        showError('Something went wrong while searching. Please try again. (' + escapeHtml(err.message || '') + ')');
      }
    } finally {
      setSearching(false);
    }
  }

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */

  function renderSkeletons(count) {
    const rows = Array.from({ length: Math.max(1, count) }, () =>
      '<div class="obg-cs-skeleton-row"></div>'
    ).join('');
    return `<div class="obg-cs-skeleton" aria-hidden="true">${rows}</div>`;
  }

  function renderResults(cardResults, unrecognized) {
    let foundCount   = 0;
    let partialCount = 0;
    let missCount    = 0;

    cardResults.forEach((r, idx) => {
      const hasAnyPrinting     = r.printings.length > 0;
      const hasInStockPrinting = r.printings.some(p => p.inStock);

      if (!hasAnyPrinting || r.error) {
        missCount++;
      } else if (hasInStockPrinting) {
        foundCount++;
      } else {
        partialCount++;
      }

      // Init row state — cap requested qty to available stock
      const firstInStock      = hasInStockPrinting ? r.printings.find(p => p.inStock) : null;
      const firstVariant      = firstInStock ? firstInStock.variants[0] : null;
      const firstVariantStock = firstVariant ? firstVariant.quantityAvailable : 0;
      const stockKnown        = firstVariantStock !== null && firstVariantStock !== undefined;
      rowState[idx] = {
        printingIdx:  0,
        variantId:    firstVariant ? firstVariant.id : null,
        maxQty:       stockKnown ? firstVariantStock : Infinity,
        qty:          stockKnown ? Math.min(r.parsed.qty, firstVariantStock) || 1 : r.parsed.qty || 1,
        requestedQty: r.parsed.qty,
        stockKnown,
        price:        firstVariant ? firstVariant.price.amount : null,
        currency:     firstVariant ? firstVariant.price.currencyCode : null,
      };
    });

    // Warning banner for unrecognized lines
    const warningHtml = unrecognized.length ? `
      <div class="obg-cs-warning">
        <strong>${unrecognized.length} line${unrecognized.length !== 1 ? 's' : ''} couldn't be read</strong>
        and ${unrecognized.length !== 1 ? 'were' : 'was'} skipped. Expected format: <code>4 Card Name (SET) *F*</code>
        <ul class="obg-cs-warning-lines">
          ${unrecognized.map(u => `<li>${escapeHtml(u.raw)}</li>`).join('')}
        </ul>
      </div>
    ` : '';

    const totalShown = cardResults.length + unrecognized.length;
    const summaryHtml = `
      <div class="obg-cs-summary">
        <span class="obg-cs-summary-text">
          Showing ${totalShown} line${totalShown !== 1 ? 's' : ''}
        </span>
        <div class="obg-cs-badges">
          ${foundCount      ? `<span class="obg-cs-badge obg-cs-badge--found">${foundCount} in stock</span>` : ''}
          ${partialCount    ? `<span class="obg-cs-badge obg-cs-badge--partial">${partialCount} out of stock</span>` : ''}
          ${missCount       ? `<span class="obg-cs-badge obg-cs-badge--miss">${missCount} not found</span>` : ''}
          ${unrecognized.length ? `<span class="obg-cs-badge obg-cs-badge--warn">${unrecognized.length} unrecognized</span>` : ''}
        </div>
      </div>
    `;

    const cardRowsHtml = cardResults
      .map((r, idx) => renderRow(r, idx))
      .join('');

    const unrecognizedRowsHtml = unrecognized.map((u, uIdx) => `
      <div class="obg-cs-row obg-cs-row--oos obg-cs-row--unrecognized" data-status="unrecognized" id="obg-cs-urow-${uIdx}">
        <div>
          <p class="obg-cs-row-name obg-cs-row-name--raw">${escapeHtml(u.raw)}</p>
          <p class="obg-cs-row-meta">Couldn't read this line</p>
        </div>
        <div class="obg-cs-row-controls">
          <span class="obg-cs-row-status">Unrecognized format</span>
        </div>
        <button class="obg-cs-row-remove" data-remove-unrec="${uIdx}" aria-label="Remove from list" title="Remove from list">×</button>
      </div>
    `).join('');

    const footerHtml = `
      <div class="obg-cs-footer">
        <div class="obg-cs-footer-text">
          <div class="obg-cs-footer-info">${foundCount} card${foundCount !== 1 ? 's' : ''} ready to add to cart</div>
          <div class="obg-cs-footer-total" id="obg-cs-total">Total: $0.00</div>
          <div class="obg-cs-toast" id="obg-cs-toast">All in-stock cards added to cart!</div>
        </div>
        <button
          class="obg-cs-btn obg-cs-btn--add-all"
          id="obg-cs-add-all"
          ${foundCount === 0 ? 'disabled' : ''}
        >Add all in-stock to cart</button>
      </div>
    `;

    results.innerHTML = warningHtml + summaryHtml +
      `<div class="obg-cs-card-list">${cardRowsHtml}${unrecognizedRowsHtml}</div>` +
      footerHtml;

    cardResults.forEach((r, idx) => bindRowEvents(r, idx));
    document.getElementById('obg-cs-add-all')?.addEventListener('click', handleAddAll);
    bindRemoveHandlers();
    recomputeTotal();
  }

  function recomputeTotal() {
    let total = 0;
    let currency = 'USD';
    Object.keys(rowState).forEach(idx => {
      const s = rowState[idx];
      if (!s || !s.variantId || s.price == null) return;
      const row = document.getElementById(`obg-cs-row-${idx}`);
      if (!row || row.classList.contains('obg-cs-row--removing')) return;
      total += parseFloat(s.price) * (Number(s.qty) || 0);
      if (s.currency) currency = s.currency;
    });
    const el = document.getElementById('obg-cs-total');
    if (el) el.textContent = 'Total: ' + formatMoney({ amount: total, currencyCode: currency });
  }

  function bindRemoveHandlers() {
    results.querySelectorAll('.obg-cs-row-remove').forEach(btn => {
      btn.addEventListener('click', () => {
        const row = btn.closest('.obg-cs-row');
        if (!row) return;
        const rowIdx = btn.dataset.removeRow;
        row.classList.add('obg-cs-row--removing');
        setTimeout(() => {
          row.remove();
          if (rowIdx !== undefined) delete rowState[rowIdx];
          recomputeSummary();
        }, 200);
      });
    });
  }

  function recomputeSummary() {
    const counts = { found: 0, partial: 0, miss: 0, unrecognized: 0 };
    let readyCount = 0;
    results.querySelectorAll('.obg-cs-row:not(.obg-cs-row--removing)').forEach(row => {
      const s = row.dataset.status;
      if (counts[s] !== undefined) counts[s]++;
      if (s === 'found') {
        const idx = row.id.replace('obg-cs-row-', '');
        const rs  = rowState[idx];
        if (rs && (Number(rs.qty) || 0) > 0) readyCount++;
      }
    });
    const total = counts.found + counts.partial + counts.miss + counts.unrecognized;

    const summaryEl = results.querySelector('.obg-cs-summary');
    if (summaryEl) {
      summaryEl.innerHTML = `
        <span class="obg-cs-summary-text">
          Showing ${total} line${total !== 1 ? 's' : ''}
        </span>
        <div class="obg-cs-badges">
          ${counts.found        ? `<span class="obg-cs-badge obg-cs-badge--found">${counts.found} in stock</span>` : ''}
          ${counts.partial      ? `<span class="obg-cs-badge obg-cs-badge--partial">${counts.partial} out of stock</span>` : ''}
          ${counts.miss         ? `<span class="obg-cs-badge obg-cs-badge--miss">${counts.miss} not found</span>` : ''}
          ${counts.unrecognized ? `<span class="obg-cs-badge obg-cs-badge--warn">${counts.unrecognized} unrecognized</span>` : ''}
        </div>
      `;
    }

    const footerInfo = results.querySelector('.obg-cs-footer-info');
    if (footerInfo) {
      footerInfo.textContent = `${readyCount} card${readyCount !== 1 ? 's' : ''} ready to add to cart`;
    }
    const addAllBtn = document.getElementById('obg-cs-add-all');
    if (addAllBtn) addAllBtn.disabled = readyCount === 0;

    recomputeTotal();
  }

  function renderRow(cardResult, idx) {
    const { parsed, printings, error } = cardResult;
    const hasInStock = printings.some(p => p.inStock);
    const isOOS      = !hasInStock;
    const notFound   = error || printings.length === 0;
    const status     = notFound ? 'miss' : isOOS ? 'partial' : 'found';
    const reqFoilTag = parsed.foil ? ' · Foil requested' : '';
    const setTag     = parsed.setName ? ` · ${escapeHtml(parsed.setName)}` : parsed.setCode ? ` · ${escapeHtml(parsed.setCode)}` : '';
    // Initial treatment label reflects the first in-stock printing (or null if OOS/not-found).
    // Updated on printing dropdown change to match the selected printing.
    const firstInStockTreatment = (printings.find(p => p.inStock) || {}).treatment || null;
    const treatmentHtml = firstInStockTreatment
      ? ` <span class="obg-cs-row-treatment" id="obg-cs-treatment-${idx}">(${escapeHtml(firstInStockTreatment)})</span>`
      : `<span class="obg-cs-row-treatment" id="obg-cs-treatment-${idx}"></span>`;

    let controlsHtml = '';

    if (error || printings.length === 0) {
      controlsHtml = `<span class="obg-cs-row-status">Not found in catalog</span>`;
    } else if (isOOS) {
      controlsHtml = `<span class="obg-cs-row-status">Out of stock</span>`;
    } else {
      const inStockPrintings = printings.filter(p => p.inStock);

      // Set dropdown (only show if multiple printings)
      const setPrintingSelect = inStockPrintings.length > 1
        ? `<select class="obg-cs-select obg-cs-select--printing" data-row="${idx}" aria-label="Choose printing">
            ${inStockPrintings.map((p, pi) =>
              `<option value="${pi}">${escapeHtml(p.setName)}</option>`
            ).join('')}
           </select>`
        : `<span class="obg-cs-set-label">${escapeHtml(inStockPrintings[0].setName)}</span>`;

      // Variant dropdown
      const firstPrinting = inStockPrintings[0];
      const variantSelect = buildVariantSelect(idx, firstPrinting.variants);

      // Price display
      const firstVariant = firstPrinting.variants[0];
      const priceHtml    = `<span class="obg-cs-price" id="obg-cs-price-${idx}">${formatMoney(firstVariant.price)}</span>`;

      // Qty — capped to available stock, with shortfall indicator
      const stockQty      = firstVariant.quantityAvailable;
      const stockKnown    = stockQty !== null && stockQty !== undefined;
      const cappedQty     = rowState[idx] ? rowState[idx].qty : (stockKnown ? Math.min(parsed.qty, stockQty) : parsed.qty) || 1;
      const stockLabel    = stockKnown ? (stockQty === 1 ? '1 in stock' : `${stockQty} in stock`) : 'In stock';
      const shortfall     = stockKnown ? parsed.qty - cappedQty : 0;
      const shortfallHtml = shortfall > 0
        ? `<span class="obg-cs-shortfall" id="obg-cs-shortfall-${idx}">Only ${cappedQty} of ${parsed.qty} requested available</span>`
        : `<span class="obg-cs-shortfall" id="obg-cs-shortfall-${idx}" style="display:none;"></span>`;

      const qtyHtml = `
        <div class="obg-cs-qty-wrap">
          <div class="obg-cs-qty">
            <button class="obg-cs-qty-btn" data-row="${idx}" data-delta="-1" aria-label="Decrease quantity">−</button>
            <span class="obg-cs-qty-val" id="obg-cs-qty-${idx}">${cappedQty}</span>
            <button class="obg-cs-qty-btn" data-row="${idx}" data-delta="1" aria-label="Increase quantity">+</button>
          </div>
          <span class="obg-cs-stock-label" id="obg-cs-stock-${idx}">${stockLabel}</span>
          ${shortfallHtml}
        </div>
      `;

      const addBtn = `
        <button class="obg-cs-btn obg-cs-btn--sm obg-cs-btn--primary obg-cs-btn-add" data-row="${idx}">
          <span class="obg-cs-btn-text">Add</span>
          <span class="obg-cs-btn-spinner" aria-hidden="true"></span>
        </button>
        <span class="obg-cs-added-confirm" id="obg-cs-confirm-${idx}">Added!</span>
      `;

      controlsHtml = setPrintingSelect + variantSelect + priceHtml + qtyHtml + addBtn;
    }

    return `
      <div class="obg-cs-row ${isOOS || notFound ? 'obg-cs-row--oos' : ''}" id="obg-cs-row-${idx}" data-status="${status}">
        <div>
          <p class="obg-cs-row-name">${escapeHtml(parsed.name)}${treatmentHtml}</p>
          <p class="obg-cs-row-meta">${parsed.qty}x requested${setTag}${reqFoilTag}</p>
        </div>
        <div class="obg-cs-row-controls" id="obg-cs-controls-${idx}">
          ${controlsHtml}
        </div>
        <button class="obg-cs-row-remove" data-remove-row="${idx}" aria-label="Remove from list" title="Remove from list">×</button>
      </div>
    `;
  }

  const CONDITION_ABBR = {
    'near mint':         'NM',
    'lightly played':    'LP',
    'moderately played': 'MP',
    'heavily played':    'HP',
    'damaged':           'DMG',
  };

  function abbreviateCondition(title) {
    const isFoil = /\bfoil\b/i.test(title);
    const base   = title.replace(/\s*foil\s*$/i, '').trim();
    const abbr   = CONDITION_ABBR[base.toLowerCase()] || base;
    return isFoil ? `${abbr} Foil` : abbr;
  }

  function buildVariantSelect(idx, variants) {
    const options = variants.map(v =>
      `<option value="${escapeHtml(v.id)}" data-price="${escapeHtml(v.price.amount)}" data-currency="${escapeHtml(v.price.currencyCode)}" data-stock="${escapeHtml(v.quantityAvailable ?? '')}">
        ${escapeHtml(abbreviateCondition(v.title))}
       </option>`
    ).join('');
    return `<select class="obg-cs-select obg-cs-select--variant" data-row="${idx}" aria-label="Choose condition">${options}</select>`;
  }

  function bindRowEvents(cardResult, idx) {
    const controls = document.getElementById(`obg-cs-controls-${idx}`);
    if (!controls) return;

    const inStockPrintings = cardResult.printings.filter(p => p.inStock);

    // Printing select
    const printingSelect = controls.querySelector('.obg-cs-select--printing');
    if (printingSelect) {
      printingSelect.addEventListener('change', () => {
        const pi       = parseInt(printingSelect.value, 10);
        const printing = inStockPrintings[pi];
        rowState[idx].printingIdx = pi;

        const treatmentEl = document.getElementById(`obg-cs-treatment-${idx}`);
        if (treatmentEl) {
          treatmentEl.textContent = printing.treatment ? ` (${printing.treatment})` : '';
        }

        const variantSelect = controls.querySelector('.obg-cs-select--variant');
        if (variantSelect) {
          variantSelect.innerHTML = printing.variants.map(v =>
            `<option value="${escapeHtml(v.id)}" data-price="${escapeHtml(v.price.amount)}" data-currency="${escapeHtml(v.price.currencyCode)}" data-stock="${escapeHtml(v.quantityAvailable ?? '')}">
              ${escapeHtml(abbreviateCondition(v.title))}
             </option>`
          ).join('');
          const first      = printing.variants[0];
          const stock      = first.quantityAvailable;
          const stockKnown = stock !== null && stock !== undefined;
          rowState[idx].variantId  = first.id;
          rowState[idx].maxQty     = stockKnown ? stock : Infinity;
          rowState[idx].stockKnown = stockKnown;
          rowState[idx].qty        = stockKnown ? Math.min(rowState[idx].qty || 1, stock) || 1 : rowState[idx].qty || 1;
          rowState[idx].price      = first.price.amount;
          rowState[idx].currency   = first.price.currencyCode;
          updatePrice(idx, first.price);
          updateStockLabel(idx, first.quantityAvailable);
          updateShortfall(idx);
          const qtyEl = document.getElementById(`obg-cs-qty-${idx}`);
          if (qtyEl) qtyEl.textContent = rowState[idx].qty;
          recomputeTotal();
        }
      });
    }

    // Variant select
    const variantSelect = controls.querySelector('.obg-cs-select--variant');
    if (variantSelect) {
      variantSelect.addEventListener('change', () => {
        const opt        = variantSelect.options[variantSelect.selectedIndex];
        const rawStock   = opt.dataset.stock;
        const stock      = rawStock !== '' ? parseInt(rawStock, 10) : null;
        const stockKnown = stock !== null;
        rowState[idx].variantId  = variantSelect.value;
        rowState[idx].maxQty     = stockKnown ? stock : Infinity;
        rowState[idx].stockKnown = stockKnown;
        rowState[idx].qty        = stockKnown ? Math.min(rowState[idx].qty || 1, stock) || 1 : rowState[idx].qty || 1;
        rowState[idx].price      = opt.dataset.price;
        rowState[idx].currency   = opt.dataset.currency;
        updatePrice(idx, { amount: opt.dataset.price, currencyCode: opt.dataset.currency });
        updateStockLabel(idx, stock);
        updateShortfall(idx);
        const qtyEl = document.getElementById(`obg-cs-qty-${idx}`);
        if (qtyEl) qtyEl.textContent = rowState[idx].qty;
        recomputeTotal();
      });
      if (variantSelect.options.length > 0) {
        const opt      = variantSelect.options[0];
        const rawStock = opt.dataset.stock;
        const stock    = rawStock !== '' ? parseInt(rawStock, 10) : null;
        rowState[idx].variantId  = variantSelect.value;
        rowState[idx].maxQty     = stock !== null ? stock : Infinity;
        rowState[idx].stockKnown = stock !== null;
      }
    }

    // Qty buttons — respect maxQty; allow 0 so users can opt out of a row
    controls.querySelectorAll('.obg-cs-qty-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const delta  = parseInt(btn.dataset.delta, 10);
        const maxQty = rowState[idx].maxQty || 1;
        rowState[idx].qty = Math.min(maxQty, Math.max(0, (rowState[idx].qty ?? 0) + delta));
        const qtyEl = document.getElementById(`obg-cs-qty-${idx}`);
        if (qtyEl) qtyEl.textContent = rowState[idx].qty;
        const row = document.getElementById(`obg-cs-row-${idx}`);
        if (row) row.classList.toggle('obg-cs-row--zero', rowState[idx].qty === 0);
        const rowAddBtn = controls.querySelector('.obg-cs-btn-add');
        if (rowAddBtn) rowAddBtn.disabled = rowState[idx].qty === 0;
        recomputeSummary();
      });
    });

    // Individual add to cart
    const addBtn = controls.querySelector('.obg-cs-btn-add');
    if (addBtn) {
      addBtn.addEventListener('click', () => handleAddOne(idx, addBtn));
    }
  }

  function updatePrice(idx, price) {
    const el = document.getElementById(`obg-cs-price-${idx}`);
    if (el) el.textContent = formatMoney(price);
  }

  function updateStockLabel(idx, stock) {
    const el = document.getElementById(`obg-cs-stock-${idx}`);
    if (!el) return;
    const known = stock !== null && stock !== undefined;
    el.textContent = !known ? 'In stock' : stock === 1 ? '1 in stock' : `${stock} in stock`;
  }

  function updateShortfall(idx) {
    const el = document.getElementById(`obg-cs-shortfall-${idx}`);
    if (!el || !rowState[idx]) return;
    const { qty, requestedQty, stockKnown } = rowState[idx];
    if (stockKnown && requestedQty > qty) {
      el.textContent   = `Only ${qty} of ${requestedQty} requested available`;
      el.style.display = '';
    } else {
      el.style.display = 'none';
    }
  }

  /* ─────────────────────────────────────────────
     CART
  ───────────────────────────────────────────── */

  function numericVariantId(gid) {
    return gid.split('/').pop();
  }

  function cartAdd(items) {
    return fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ items }),
    }).then(r => {
      if (!r.ok) throw new Error('Cart add failed: ' + r.status);
      return r.json();
    });
  }

  async function handleAddOne(idx, btn) {
    const state = rowState[idx];
    if (!state?.variantId || !state.qty) return;

    setButtonLoading(btn, true);
    try {
      await cartAdd([{ id: numericVariantId(state.variantId), quantity: state.qty }]);
      showAddedConfirm(idx);
      dispatchCartUpdate();
    } catch (err) {
      console.error('OBG add to cart error:', err);
      alert('Could not add to cart. Please try again.');
    } finally {
      setButtonLoading(btn, false);
    }
  }

  async function handleAddAll() {
    const btn = document.getElementById('obg-cs-add-all');
    if (!btn) return;

    const toAdd = Object.values(rowState)
      .filter(s => s.variantId && (Number(s.qty) || 0) > 0)
      .map(s => ({ id: numericVariantId(s.variantId), quantity: s.qty }));

    if (!toAdd.length) return;

    btn.disabled    = true;
    btn.textContent = 'Adding...';

    try {
      await cartAdd(toAdd);
      const toast = document.getElementById('obg-cs-toast');
      if (toast) {
        toast.classList.add('obg-cs-toast--visible');
        setTimeout(() => toast.classList.remove('obg-cs-toast--visible'), 4000);
      }
      dispatchCartUpdate();
    } catch (err) {
      console.error('OBG add all error:', err);
      alert('Could not add all cards to cart. Please try again.');
    } finally {
      btn.disabled    = false;
      btn.textContent = 'Add all in-stock to cart';
    }
  }

  function dispatchCartUpdate() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('theme:cart:added'));
  }

  /* ─────────────────────────────────────────────
     UI HELPERS
  ───────────────────────────────────────────── */

  function setSearching(on) {
    searchBtn.disabled = on;
    searchBtn.classList.toggle('obg-cs-btn--loading', on);
  }

  function setButtonLoading(btn, on) {
    btn.disabled = on;
    btn.classList.toggle('obg-cs-btn--loading', on);
  }

  function showAddedConfirm(idx) {
    const el = document.getElementById(`obg-cs-confirm-${idx}`);
    if (!el) return;
    el.classList.add('obg-cs-added-confirm--visible');
    setTimeout(() => el.classList.remove('obg-cs-added-confirm--visible'), 2500);
  }

  function showError(msg) {
    results.innerHTML = `<div class="obg-cs-error">${msg}</div>`;
  }

  function formatMoney(price) {
    const amount   = parseFloat(price.amount || price);
    const currency = price.currencyCode || 'USD';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
    } catch {
      return '$' + amount.toFixed(2);
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ─────────────────────────────────────────────
     EVENT BINDINGS
  ───────────────────────────────────────────── */

  searchBtn.addEventListener('click', runSearch);

  input.addEventListener('keydown', e => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') runSearch();
  });

  clearBtn.addEventListener('click', () => {
    input.value = '';
    results.innerHTML = '';
    input.focus();
  });

})();
